import {
  Alert,
  AppLayout,
  BreadcrumbGroup,
  Button,
  ContentLayout,
  Flashbar,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-react';
import {
  EventInput,
  EventType,
  LocationType,
  ShiftInput,
  ShiftTimingType,
  Visibility,
} from '@amzn/red-velvet-api';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalizedText } from '../hooks/localizedText';
import { useNavigate, useSearchParams, useOutletContext } from 'react-router-dom';
import { EventPreviewModal } from '../components/events/createEvent/components/EventPreviewModal';
import { CancelDraftModal } from '../components/events/createEvent/components/cancelDraftModal';
import { DiscardChangesModal } from '../components/events/createEvent/components/discardChangesModal';
import { useWizardToolsPanel } from '../hooks/useWizardToolsPanel';
import {
  getEventDetailsStep,
  getPublishStep,
  getShiftStep,
  getTeamAndPartnershipStep,
} from '../components/events/createEvent/steps';
import { AutoSaveStatusLabel } from '../components/events/wizard/AutoSaveStatusIndicator';
import { CreateEventWizard } from '../components/events/wizard/createEventWizard';
import { useDeleteEvent } from '../hooks/events/deleteEvent';
import { usePublishEvent } from '../hooks/events/publishEvent';
import { useAutoSaveEventDraft } from '../hooks/events/useAutoSaveEventDraft';
import { useEventDraftPrePopulation } from '../hooks/events/useEventDraftPrePopulation';
import { useWizardStrings } from '../hooks/localization/wizardStrings';
import { useErrorMessages } from '../hooks/localization/errorMessages';
import { useUnsavedChangesBrowserNavigationPrompt } from '../hooks/unsavedChanges';
import {
  validateEventDetails,
  validateShifts,
  validateTeamAndPartnership,
} from '../utils/eventValidation';
import {
  SAVE_STATUS_SAVING,
  COORDINATOR_SIGNUP_NOTIFICATIONS_INTERNAL,
  SHIFT_WAITLIST,
  NAVIGATION_REDESIGN,
  EVENT_CREATION,
} from '../utils/constants';
import { GRANT_PREFILL_PARAM_KEYS, parseEventPrefill } from '../utils/eventPrefill';
import { useCreatorContact } from '../hooks/events/useCreatorContact';
import { publishKatalMetric } from '../components/katalAnalytics';
import { useFeatures, featureIsEnabled } from '../hooks/features';
import { applyWaitlistEnabledDefault } from '../utils/waitlistUtils';
import { AppOutletContext } from '../App';
import { urlWithPersistantParams } from '../components/SpaLink';

export function CreateEvent() {
  const namespaces = ['events', 'translation'];
  const { t } = useLocalizedText(namespaces);
  const wizardStrings = useWizardStrings('createEventWizard', namespaces);
  const { resolveError } = useErrorMessages();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const features = useFeatures();
  const coordinatorNotificationsEnabled = featureIsEnabled(
    COORDINATOR_SIGNUP_NOTIFICATIONS_INTERNAL,
    features,
    searchParams
  );
  const shiftWaitlistEnabled = featureIsEnabled(SHIFT_WAITLIST, features, searchParams);
  const urlFeaturesParam = searchParams.get('features');
  const coordinatorPath = (extraParams?: string) => {
    const base = `/coordinator-activity${urlFeaturesParam ? '?features=' + urlFeaturesParam : ''}`;
    return extraParams ? `${base}${urlFeaturesParam ? '&' : '?'}${extraParams}` : base;
  };

  const eventId = useRef('');

  const [page, setPage] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { mutate: publishEvent, isPending: isPublishing } = usePublishEvent();
  const { mutate: deleteEvent, isPending: isDeleting } = useDeleteEvent();
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [hasEventId, setHasEventId] = useState(false);
  const [showPublishError, setShowPublishError] = useState(false);

  const isNewNav = featureIsEnabled(NAVIGATION_REDESIGN, features, searchParams);
  const outletContext = useOutletContext<AppOutletContext | null>();
  // Pass outletContext as controller so the hook delegates open/close to AppLayout.
  // This prevents the reopen-loop bug where child useEffects fought with AppLayout's state.
  const wizardTools = useWizardToolsPanel(isNewNav ? outletContext : null);

  // Clean up tools panel on unmount (new nav mode)
  useEffect(() => {
    return () => {
      if (isNewNav && outletContext) {
        outletContext.setTools(null);
        outletContext.setToolsOpen(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewNav]);

  useUnsavedChangesBrowserNavigationPrompt(hasUnsavedChanges);

  // Cloudscape unsaved-changes pattern: block SPA navigation (side nav, breadcrumbs, top-nav logo)
  // while the wizard has unsaved edits. useUnsavedChangesBrowserNavigationPrompt only covers
  // browser-level navigation (tab close, back button); SPA clicks bypass it, so a side-nav click
  // mid-wizard would silently discard work (CR-291797692 rev 8). The guard captures the target
  // href, shows the discard modal, and blocks; confirming navigates to the captured href.
  const [discardModalVisible, setDiscardModalVisible] = useState(false);
  const [pendingNavHref, setPendingNavHref] = useState<string | null>(null);
  const navigationGuard = useCallback(
    (href: string) => {
      if (hasUnsavedChanges) {
        setPendingNavHref(href);
        setDiscardModalVisible(true);
        return false; // block — the discard modal handles the decision
      }
      return true;
    },
    [hasUnsavedChanges]
  );

  useEffect(() => {
    if (isNewNav && outletContext) {
      outletContext.setNavigationGuard(navigationGuard);
    }
    return () => {
      if (isNewNav && outletContext) {
        outletContext.setNavigationGuard(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewNav, navigationGuard]);

  const { autoSaveHandler, autoSaveShiftsHandler, saveDraft, saveStatus, lastSavedTime } =
    useAutoSaveEventDraft({
      eventId,
      metricPrefix: 'createEvent',
      setHasUnsavedChanges,
      onSaveError: (error) => {
        const message = error.message
          ? t('error.draftNotSavedMsgWithDetail', { detail: resolveError(error) })
          : t('error.draftNotSavedMsg');
        setErrors((prev) => ({ ...prev, draftSaveError: message }));
      },
      onEventCreated: () => setHasEventId(true),
      onShiftIdsSynced: (shiftIds) => {
        setShiftStepField((prev) =>
          // Materialize the waitlist default into LOCAL state before stamping the new shiftId, so a
          // newly-created capped shift carries an explicit waitlistEnabled that matches the persisted
          // value. The display default already keeps it ON via isCreateFlow; materializing here makes
          // local state explicit (defense-in-depth) rather than relying solely on the derived default.
          applyWaitlistEnabledDefault(prev, shiftWaitlistEnabled, true).map((shift, i) =>
            shiftIds[i] ? { ...shift, shiftId: shiftIds[i] } : shift
          )
        );
      },
    });

  const draftId = searchParams.get('draftId');
  const initialDraftId = useRef(draftId);

  // VOLUNTEERCENTER-777: seed the initial event from allowlisted URL params (deep-link prefill).
  // draftId always wins — when resuming a saved draft, prefill params are ignored entirely.
  const grantFieldsEnabled = featureIsEnabled(EVENT_CREATION, features, searchParams);
  const prefill = useMemo(
    () =>
      draftId
        ? { eventInput: {}, source: undefined, appliedParams: [] as string[] }
        : parseEventPrefill(searchParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- computed once from the initial URL
    []
  );
  // Grant fields are gated behind EVENT_CREATION; drop grant prefill when the flag is off.
  const prefillEventInput = { ...prefill.eventInput };
  let appliedPrefillParams = prefill.appliedParams;
  if (!grantFieldsEnabled) {
    delete prefillEventInput.grantId;
    delete prefillEventInput.usedAciGrant;
    // Params dropped here never reached the form, so they must not count as applied — otherwise the
    // attribution metric over-reports grant links for everyone without the flag.
    appliedPrefillParams = appliedPrefillParams.filter(
      (param) => !GRANT_PREFILL_PARAM_KEYS.includes(param)
    );
  }

  const initialEventDetailsStepField: EventInput = {
    title: '',
    description: '',
    shortDescription: '',
    location: { locationType: LocationType.ON_SITE },
    focusArea: '',
    primaryActivity: '',
    eventType: EventType.VOLUNTEERING,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    visibility: Visibility.PUBLIC,
    // Seeded fields remain fully editable in the wizard.
    ...prefillEventInput,
  };

  const [eventDetailsStepField, setEventDetailsStepField] = useState<EventInput>(
    initialEventDetailsStepField
  );

  const initialShiftStepField: ShiftInput[] = [
    {
      startDateTime: new Date(new Date().toDateString()),
      endDateTime: new Date(new Date().toDateString()),
      useEventLocation: true,
      shiftTimingType: ShiftTimingType.SCHEDULED,
      shiftLength: 0,
      // No waitlistEnabled seed: a new shift starts unlimited. ShiftDetailsComponent derives the
      // toggle default from the absence of a shiftId (new shift -> ON once capped), and
      // applyWaitlistEnabledDefault materializes the outbound value on submit. Seeding true here
      // would be auto-off'd to false while still unlimited and then wrongly read OFF once capped.
    },
  ];
  const [shiftStepField, setShiftStepField] = useState<ShiftInput[]>(initialShiftStepField);

  const [hasPrePopulated, setHasPrePopulated] = useState(false);
  const {
    eventInput: draftEventInput,
    shiftInputs: draftShiftInputs,
    ownerAlias: draftOwnerAlias,
    eventStatus: draftEventStatus,
    isLoading: isDraftLoading,
    error: draftError,
    isNotFound: isDraftNotFound,
  } = useEventDraftPrePopulation(initialDraftId.current);

  useEffect(() => {
    if (draftId) {
      eventId.current = draftId;
      setHasEventId(true);
    }
  }, [draftId]);

  useEffect(() => {
    if (draftEventInput && !isDraftLoading && !hasPrePopulated) {
      setEventDetailsStepField(draftEventInput);
      setShiftStepField(draftShiftInputs.length > 0 ? draftShiftInputs : initialShiftStepField);
      setHasPrePopulated(true);
      publishKatalMetric('createEvent_draftResumed', eventId.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- draftShiftInputs is a new ref each render; draftEventInput + isDraftLoading gate this effect
  }, [draftEventInput, isDraftLoading]);

  useEffect(() => {
    if (initialDraftId.current && isDraftNotFound) {
      publishKatalMetric('createEvent_draftAlertInvalidDraftId', initialDraftId.current);
    }
  }, [isDraftNotFound]);

  // VOLUNTEERCENTER-777: record deep-link prefill usage for entry-point attribution.
  useEffect(() => {
    if (appliedPrefillParams.length > 0) {
      publishKatalMetric('createEvent_prefill', appliedPrefillParams.join(','));
      if (prefill.source) {
        publishKatalMetric('createEvent_prefillSource', prefill.source);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once on mount
  }, []);

  // Resumed drafts already have the creator contact, no need to autosave it again.
  useCreatorContact(setEventDetailsStepField, initialDraftId.current ? undefined : autoSaveHandler);

  const validateAllSteps = (): Record<string, string> => {
    const allErrors = {
      ...validateEventDetails(eventDetailsStepField, t),
      ...validateShifts(shiftStepField, t),
      ...validateTeamAndPartnership(eventDetailsStepField, t, { coordinatorNotificationsEnabled }),
    };
    setErrors(allErrors);
    return allErrors;
  };

  const updateEventFieldHandler = (eventInput: EventInput) => {
    setEventDetailsStepField(eventInput);
    setErrors((prev) => {
      const remaining = validateEventDetails(eventInput, t);
      const teamRemaining = validateTeamAndPartnership(eventInput, t, {
        coordinatorNotificationsEnabled,
      });
      const next = { ...prev };
      // Clear event/team errors that are now resolved, keep shift and publish errors
      for (const key of Object.keys(next)) {
        if (
          !key.startsWith('shift_') &&
          key !== 'publishError' &&
          !remaining[key] &&
          !teamRemaining[key]
        ) {
          delete next[key];
        }
      }
      return next;
    });
    autoSaveHandler(eventInput);
  };

  const updateShiftFieldHandler = (
    shiftInput: ShiftInput[],
    changedIndex?: number,
    removedIndex?: number
  ) => {
    setShiftStepField(shiftInput);

    // Error keys are prefixed "shift_{index}_*". When a shift is removed, drop its
    // errors and re-index higher shifts down by one. When a shift is edited, clear
    // only that shift's errors so stale messages don't persist.
    if (removedIndex != null) {
      setErrors((prev) => {
        const next: Record<string, string> = {};
        for (const [key, value] of Object.entries(prev)) {
          const match = key.match(/^shift_(\d+)_(.+)$/);
          if (!match) {
            next[key] = value;
          } else {
            const i = Number(match[1]);
            const field = match[2];
            if (i < removedIndex) next[key] = value;
            else if (i > removedIndex) next[`shift_${i - 1}_${field}`] = value;
            // i === removedIndex: drop (deleted shift)
          }
        }
        return next;
      });
    } else if (changedIndex != null) {
      setErrors((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          if (key.startsWith(`shift_${changedIndex}_`)) delete next[key];
        }
        return next;
      });
    }

    autoSaveShiftsHandler(applyWaitlistEnabledDefault(shiftInput, shiftWaitlistEnabled, true));
  };

  const hasValidationErrors =
    showPublishError && Object.keys(errors).some((key) => key !== 'publishError');

  const steps = [
    getEventDetailsStep(
      t('eventDetailsStep.eventDetails'),
      t('eventDetailsStep.eventDetailsSubText'),
      eventDetailsStepField,
      updateEventFieldHandler,
      'createEvent',
      errors,
      wizardTools.openGrantPanel,
      wizardTools.openCommunityOrganizationPanel,
      eventId.current || undefined
    ),
    getShiftStep(
      t('shiftStep.stepTitle'),
      t('shiftStep.headerDescription'),
      shiftStepField,
      updateShiftFieldHandler,
      eventDetailsStepField,
      updateEventFieldHandler,
      'createEvent',
      errors,
      undefined,
      wizardTools.openShiftTypePanel,
      { onInfoClick: wizardTools.openWaitlistPanel },
      true
    ),
    getTeamAndPartnershipStep(
      t('teamPartnershipStep.stepTitle'),
      t('teamPartnershipStep.stepDescription'),
      eventDetailsStepField,
      updateEventFieldHandler,
      'createEvent',
      errors,
      draftOwnerAlias,
      draftEventStatus,
      undefined,
      wizardTools.openCoordinatorNotificationPanel
    ),
    getPublishStep(
      t('reviewPublishStep.stepTitle'),
      t('reviewPublishStep.preview'),
      () => {
        publishKatalMetric('createEvent_previewModalOpened', eventId.current);
        setIsPreviewModalVisible(true);
      },
      {
        currentEventFields: eventDetailsStepField,
        currentShiftFields: shiftStepField,
        updateShiftFieldHandler: updateShiftFieldHandler,
        updateEventFieldHandler: updateEventFieldHandler,
        setActiveStepIndex: (index: number) => setPage(index),
        errors,
        metricPrefix: 'createEvent',
        ownerAlias: draftOwnerAlias,
        eventStatus: draftEventStatus,
        onDismissPublishError: () =>
          setErrors((prev) => {
            const { publishError, ...rest } = prev;
            return rest;
          }),
      },
      hasValidationErrors ? t('error.resolveRequiredFields') : undefined
    ),
  ].map((step) => ({
    ...step,
    content: (
      <SpaceBetween direction="vertical" size="l">
        {step.content}
        {errors.draftSaveError && (
          <Alert
            type="error"
            header={t('error.draftNotSavedHeader')}
            dismissible
            onDismiss={() =>
              setErrors((prev) => {
                const { draftSaveError, ...rest } = prev;
                return rest;
              })
            }
          >
            {errors.draftSaveError}
          </Alert>
        )}
        {errors.cancelError && (
          <Alert
            type="error"
            header={t('error.deleteDraftErrorHeader')}
            dismissible
            onDismiss={() =>
              setErrors((prev) => {
                const { cancelError, ...rest } = prev;
                return rest;
              })
            }
          >
            {errors.cancelError}
          </Alert>
        )}
      </SpaceBetween>
    ),
  }));

  const isDraftResumeLoading =
    initialDraftId.current && (isDraftLoading || (!hasPrePopulated && !draftError));

  const earlyContent = isDraftResumeLoading ? (
    <Spinner size="large" />
  ) : isDraftNotFound ? (
    <Flashbar
      items={[
        {
          type: 'warning',
          header: t('error.draftNotFoundHeader'),
          content: t('error.draftNotFoundMsg'),
          action: (
            <Button
              onClick={() => {
                publishKatalMetric('createEvent_entryPoint', 'draftNotFound');
                window.location.href = '/create-event';
              }}
            >
              {t('error.draftNotFoundAction')}
            </Button>
          ),
        },
      ]}
    />
  ) : initialDraftId.current && draftError ? (
    <Flashbar items={[{ type: 'error', content: draftError }]} />
  ) : null;

  // Shared wizard element used by both new-nav and legacy render paths. Extracting
  // this into a single variable ensures both layouts render the exact same wizard —
  // including secondaryActions (Save Draft & Exit + AutoSaveStatusLabel), which the
  // legacy AppLayout previously duplicated inline and the new-nav branch was silently
  // omitting (CR-291797692 ITL Hydra failures).
  const wizardMarkup = (
    <CreateEventWizard
      metricPrefix="createEvent"
      eventId={eventId.current}
      isLoadingNextStep={isPublishing || saveStatus === SAVE_STATUS_SAVING}
      i18nStrings={{
        ...wizardStrings,
        // Next button should match the title of the next step
        nextButton: t([`createEventWizard.nextButton`], {
          title: steps[page + 1] ? steps[page + 1].title : t([`wizard.nextButton`]),
        }),
        submitButton: t('createEventWizard.submitButton'),
      }}
      steps={steps}
      onCancel={() => {
        if (!eventId.current) {
          navigate(coordinatorPath());
          return;
        }
        setErrors((prev) => {
          const { cancelError, ...rest } = prev;
          return rest;
        });
        setCancelModalVisible(true);
      }}
      onNavigate={({ detail }) => {
        setShowPublishError(false);
        setErrors((prev) => {
          const { publishError, ...rest } = prev;
          return rest;
        });
        wizardTools.close();
        setPage(detail.requestedStepIndex);
      }}
      onSubmit={() => {
        const allErrors = validateAllSteps();
        if (Object.keys(allErrors).length > 0) {
          setShowPublishError(true);
          publishKatalMetric('createEvent_validationFailure', Object.keys(allErrors).join(','));
          setTimeout(() => {
            const firstError = document.querySelector('[class*="awsui_error__message"]');
            if (firstError) {
              firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }, 0);
          return;
        }
        if (eventId.current !== '') {
          publishEvent(
            {
              eventId: eventId.current,
              syncBenevity: searchParams.get('syncBenevity') === 'true' || undefined,
            },
            {
              onSuccess: (response) => {
                publishKatalMetric('createEvent_publishSuccess', response.eventId);
                eventId.current = response.eventId;
                navigate(
                  `/view-event?id=${response.eventId}${urlFeaturesParam ? '&features=' + urlFeaturesParam : ''}`,
                  {
                    replace: true,
                    state: {
                      goBackPath: `/create-event${urlFeaturesParam ? '?features=' + urlFeaturesParam : ''}`,
                    },
                  }
                );
              },
              onError: (error) => {
                // Re-persist the in-flight draft after a failed publish so the user doesn't
                // lose their work — publishEvent finalizes the draft into a published event,
                // and on failure we need to write the state back as a draft.
                publishKatalMetric('createEvent_publishFailure', eventId.current);
                autoSaveHandler(eventDetailsStepField);
                autoSaveShiftsHandler(
                  applyWaitlistEnabledDefault(shiftStepField, shiftWaitlistEnabled, true)
                );
                const detail = resolveError(error);
                setErrors((prev) => ({
                  ...prev,
                  publishError: t('error.publishErrorMsgWithDetail', { detail }),
                }));
              },
            }
          );
        }
      }}
      activeStepIndex={page}
      secondaryActions={
        <SpaceBetween direction="horizontal" size="m" alignItems="center">
          <Button
            onClick={() => {
              publishKatalMetric('createEvent_saveDraft', eventId.current);
              saveDraft(
                eventDetailsStepField,
                applyWaitlistEnabledDefault(shiftStepField, shiftWaitlistEnabled, true),
                () => {
                  navigate(coordinatorPath('tab=draft'));
                }
              );
            }}
            disabled={!hasEventId}
            disabledReason={t('createEventWizard.saveDraftDisabledReason')}
          >
            {t('createEventWizard.saveDraft')}
          </Button>
          <AutoSaveStatusLabel saveStatus={saveStatus} lastSavedTime={lastSavedTime} />
        </SpaceBetween>
      }
    />
  );

  const wizardBody = (
    <ContentLayout>
      {earlyContent ?? (
        <SpaceBetween direction="vertical" size="l">
          {wizardMarkup}
        </SpaceBetween>
      )}
    </ContentLayout>
  );

  const modals = (
    <>
      <DiscardChangesModal
        visible={discardModalVisible}
        onDismiss={() => {
          setDiscardModalVisible(false);
          setPendingNavHref(null);
        }}
        onConfirm={() => {
          publishKatalMetric('createEvent_discardChanges', eventId.current);
          setDiscardModalVisible(false);
          setHasUnsavedChanges(false);
          // pendingNavHref is the raw href from the nav guard (e.g. "/my-activity"), so it
          // carries no query string. Route it through the same persistent-param helper the
          // SPA wrapper uses, otherwise features/timezone are dropped and the user lands
          // back in the legacy layout after discarding.
          if (pendingNavHref) navigate(urlWithPersistantParams(searchParams, pendingNavHref));
          setPendingNavHref(null);
        }}
      />
      <CancelDraftModal
        visible={cancelModalVisible}
        loading={isDeleting}
        onDismiss={() => {
          publishKatalMetric('createEvent_cancelDraftDismissed', eventId.current);
          setCancelModalVisible(false);
        }}
        onConfirm={() => {
          publishKatalMetric('createEvent_cancelDraftConfirmed', eventId.current);
          deleteEvent(
            { eventId: eventId.current },
            {
              onSuccess: () => {
                setCancelModalVisible(false);
                navigate(coordinatorPath());
              },
              onError: () => {
                setCancelModalVisible(false);
                setErrors((prev) => ({ ...prev, cancelError: t('error.deleteDraftError') }));
              },
            }
          );
        }}
      />
      <EventPreviewModal
        currentEventFields={eventDetailsStepField}
        currentShiftFields={shiftStepField}
        isVisible={isPreviewModalVisible}
        onDismiss={() => setIsPreviewModalVisible(false)}
        eventId={eventId.current}
      />
    </>
  );

  // New navigation: parent AppLayout in App.tsx owns the chrome (side nav, breadcrumbs,
  // tools panel). We render content only; tools are synced to the parent via
  // outletContext in the useEffect above.
  if (isNewNav) {
    return (
      <>
        {wizardBody}
        {modals}
      </>
    );
  }

  // Legacy: page owns its own AppLayout wrapper.
  return (
    <>
      <AppLayout
        footerSelector=".footer"
        navigationHide={true}
        toolsHide={earlyContent ? true : wizardTools.toolsHide}
        toolsOpen={wizardTools.toolsOpen}
        onToolsChange={wizardTools.onToolsChange}
        tools={wizardTools.tools}
        breadcrumbs={
          !earlyContent ? (
            <BreadcrumbGroup
              items={[
                {
                  text: t('breadcrumbs.myActivity'),
                  href: `/my-activity${urlFeaturesParam ? '?features=' + urlFeaturesParam : ''}`,
                },
                { text: t('breadcrumbs.coordinatorActivity'), href: coordinatorPath() },
                { text: t('breadcrumbs.createEvent'), href: '#' },
              ]}
            />
          ) : undefined
        }
        content={wizardBody}
        contentType="wizard"
      />
      {modals}
    </>
  );
}
